import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import colors from 'theme/_colors.scss';
import { translate } from 'helpers/translateHelper';
import { IconTick, IconDatePicker, IconInfoFilled, IconRefresh, IconCheckedFilled, IconClose } from 'svgIcons/MotionPortalIcons';
import { GeneralDialog } from 'sharedComponents/GeneralDialog';
import { SingleDateSelector } from 'sharedComponents/DateSelector';
import { RETRAINING_STATUS } from './constants';
import { getDateFormat } from 'helpers/dateTimeFormatter';


export const RetrainingBanner = ({ retrainingDetails, isPrivilegedUser, onViewProgress }) => {
    const retrainingStatus = retrainingDetails?.retrainingStatus;
    const isTraining = retrainingStatus === RETRAINING_STATUS.TRAINING;
    const isCompleted = retrainingStatus === RETRAINING_STATUS.COMPLETED;
    const isDriveAnomaly = retrainingDetails?.isDriveAnomaly || false;

    if (!isTraining && !isCompleted) {
        return null;
    }

    const displayEmail = isPrivilegedUser ? retrainingDetails?.initiatedBy : 'ABB';
    const submittedDate = retrainingDetails?.requestedSubmittedDate;
    const trainingStartDate = retrainingDetails?.retrainingStartDate;
    const formattedSubmittedDate = submittedDate ? moment(submittedDate.split('T')[0], 'YYYY-MM-DD').format('DD/MM/YYYY') : '';
    const formattedTrainingStartDate = trainingStartDate ? moment(trainingStartDate.split('T')[0], 'YYYY-MM-DD').format('DD/MM/YYYY') : '';

    if (isCompleted) {
        return (
            <div className='retraining-banner-completed'>
                <div className='retraining-banner-completed__body'>
                    <span className='retraining-banner-completed__icon'>
                        <IconCheckedFilled width={16} height={16} color={colors.jungleGreen} />
                    </span>
                    <span className='retraining-banner-completed__text'>
                        {translate('ABB.Powertrain.Frontend.retrainingCompleted')}
                    </span>
                </div>
                <div className='retraining-banner-completed__close'> <IconClose /></div>
            </div>
        );
    }

    return (
        <div className='retraining-banner'>
            <span className='retraining-banner-icon'>
                <IconInfoFilled width={16} height={16} color={colors.dodgerBlue} />
            </span>
            <div className='retraining-banner-text'>
                <span className='retraining-banner-body'>
                    {translate('ABB.Powertrain.Frontend.retrainingProgressUserInfo')}{' '}
                    <strong className='retraining-highlight'>{displayEmail}</strong>
                    {' '}{translate('ABB.Powertrain.Frontend.retrainingOn')}{' '}
                    <strong className='retraining-highlight'>
                        {formattedSubmittedDate}
                    </strong>
                    {'. '}
                    {translate('ABB.Powertrain.Frontend.retrainingStartingDateLabel')}{': '}
                    <strong className='retraining-highlight'>{formattedTrainingStartDate}</strong>
                    {'. '}{translate('ABB.Powertrain.Frontend.retrainingDetectionPaused')}
                </span>
                <span className='retraining-banner-body'>
                    {translate(isDriveAnomaly
                        ? 'ABB.Powertrain.Frontend.driveAnomalyRetrainingProcesstimeinfo'
                        : 'ABB.Powertrain.Frontend.retrainingProcesstimeinfo')}{' '}
                    <button
                        type='button'
                        className='view-progress-link'
                        onClick={onViewProgress}
                    >
                        {translate('ABB.Powertrain.Frontend.viewRetrainingProgressLabel')}
                    </button>
                </span>
            </div>
        </div>
    );
};

RetrainingBanner.propTypes = {
    retrainingDetails: PropTypes.shape({
        enableRetraining: PropTypes.bool,
        retrainingStatus: PropTypes.string,
        retrainingStartDate: PropTypes.string,
        retrainingCompletedDate: PropTypes.string,
        requestedSubmittedDate: PropTypes.string,
        initiatedBy: PropTypes.string,
        isDriveAnomaly: PropTypes.bool
    }),
    isPrivilegedUser: PropTypes.bool.isRequired,
    onViewProgress: PropTypes.func.isRequired
};

const retrainingStepKeys = ['step-date-selected', 'step-collecting-data', 'step-model-learns', 'step-detection-resumes'];

const StepCircle = ({ done, current }) => {
    if (done) {
        return <span className='step-circle step-circle--done'><IconTick width={'16px'} height={'16px'} color={'#fff'} /></span>;
    }
    if (current) {
        return <span className='step-circle step-circle--current' />;
    }
    return <span className='step-circle' />;
};

StepCircle.propTypes = {
    done: PropTypes.bool.isRequired,
    current: PropTypes.bool.isRequired
};

// --- Retrain dialog (defined outside parent to avoid sonar issue) ---
const RetrainingDialog = ({ show, onClose, onConfirm, retrainingStartDate, onDateSelect, pickerVisibility, isDriveAnomaly }) => {
    return (
        <GeneralDialog
            className='retrain-model-dialog'
            show={show}
            close={onClose}
            title={
                <div className='retraining-model-title'>
                    <IconRefresh width={'16px'} height={'16px'} />
                    <span>{translate('ABB.Powertrain.Frontend.retrainModelTitle')}</span>
                </div>
            }
            closeButton={true}
            acceptButtonProps={{
                text: translate('ABB.Powertrain.Frontend.retrainModelLabel'),
                onClick: onConfirm,
                type: 'primary',
                disabled: !retrainingStartDate
            }}
            cancelButtonProps={{
                text: translate('ABB.Powertrain.Frontend.cancelLabel'),
                onClick: onClose
            }}
            modalClassName='retrain-model-dialog'
        >
            <div className='retrain-model-info-message'>
                <span className='icon'><IconInfoFilled width={'16px'} height={'16px'} color={colors.dodgerBlue} /></span>
                <span className='title'>
                    {translate('ABB.Powertrain.Frontend.retrainModelInfoMessage')}
                </span>
            </div>

            <div className='retrain-model-recommended-scenarios'>
                <div className='section-title'>
                    {translate('ABB.Powertrain.Frontend.recommendedRetrainingScenarios')}
                </div>
                {isDriveAnomaly ?
                    <ul className='scenarios-list'>
                        <li>{translate('ABB.Powertrain.Frontend.postMaintenance')}</li>
                        <li>{translate('ABB.Powertrain.Frontend.frequentFalsePositives')}</li>
                        <li>{translate('ABB.Powertrain.Frontend.changeInOperationConditions')}</li>
                    </ul>
                    :
                    <ul className='scenarios-list'>
                        <li>{translate('ABB.Powertrain.Frontend.postOverhaul')}</li>
                        <li>{translate('ABB.Powertrain.Frontend.frequentFalsePositives')}</li>
                        <li>{translate('ABB.Powertrain.Frontend.postFaultClearance')}</li>
                    </ul>
                }
            </div>

            <div className='retrain-model-data-requirements'>
                <div className='section-title'>
                    {translate('ABB.Powertrain.Frontend.dataRequirements')}
                </div>
                {isDriveAnomaly ?
                    <ul className='requirements-list'>
                        <li>{translate('ABB.Powertrain.Frontend.driveAnomalyDataRequirement')}</li>
                        <li>{translate('ABB.Powertrain.Frontend.driveAnomalyBestPractice')}</li>
                    </ul>
                    :
                    <ul className='requirements-list'>
                        <li>{translate('ABB.Powertrain.Frontend.continuousAssetOperation')}</li>
                        <li>{translate('ABB.Powertrain.Frontend.sufficientDataCollection')}</li>
                    </ul>
                }
            </div>

            <div className='retraining__date'>
                <label>{translate('ABB.Powertrain.Frontend.retrainingStartingDateLabel')}</label>
                <SingleDateSelector
                    id='retraining-date-selector'
                    className='retraining-date-selector'
                    placeholder={getDateFormat()}
                    value={retrainingStartDate}
                    onSelect={onDateSelect}
                    visibilityControl={pickerVisibility}
                    numberOfMonths={1}
                    confirmOnSelect={true}
                    showLeftIcon={false}
                    showRightIcon={true}
                    rightIcon={<IconDatePicker className='calendar-icon' />}
                    minDate={moment().subtract(isDriveAnomaly ? 90 : 30, 'days').toDate()}
                    disableFuture={true}
                    disablePast={false}
                    excludeToday={true}
                    placement='left'
                />
                <span className='date-selection-info'>
                    {translate(isDriveAnomaly
                        ? 'ABB.Powertrain.Frontend.driveAnomalyRetrainingDateSelectionInfo'
                        : 'ABB.Powertrain.Frontend.retrainingDateSelectionPeriodInfo')}
                </span>
            </div>
        </GeneralDialog>
    );
};

RetrainingDialog.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    retrainingStartDate: PropTypes.any,
    onDateSelect: PropTypes.func.isRequired,
    pickerVisibility: PropTypes.shape({
        visible: PropTypes.bool,
        setVisibilityOn: PropTypes.func,
        setVisibilityOff: PropTypes.func
    }).isRequired,
    isDriveAnomaly: PropTypes.bool
};
// --- Retraining progress modal (defined outside parent to avoid sonar issue) ---
const RetrainingProgressModal = ({ show, onClose, retrainingDetails }) => {
    const retrainingStatus = retrainingDetails?.retrainingStatus;
    const isCompleted = retrainingStatus === RETRAINING_STATUS.COMPLETED;
    const isTraining = retrainingStatus === RETRAINING_STATUS.TRAINING;
    const isDriveAnomaly = retrainingDetails?.isDriveAnomaly || false;
    const steps = [
        {
            title: translate('ABB.Powertrain.Frontend.retrainingStep1Title'),
            description: translate(isDriveAnomaly ? 'ABB.Powertrain.Frontend.driveAnomalyStep1Description' : 'ABB.Powertrain.Frontend.retrainingStep1Description'),
            done: true,
            current: false
        },
        {
            title: translate('ABB.Powertrain.Frontend.retrainingStep2Title'),
            description: translate(isDriveAnomaly ? 'ABB.Powertrain.Frontend.driveAnomalyStep2Description' : 'ABB.Powertrain.Frontend.retrainingStep2Description'),
            done: isCompleted,
            current: isTraining
        },
        {
            title: translate('ABB.Powertrain.Frontend.retrainingStep3Title'),
            description: translate('ABB.Powertrain.Frontend.retrainingStep3Description'),
            done: isCompleted,
            current: false
        },
        {
            title: translate('ABB.Powertrain.Frontend.retrainingStep4Title'),
            description: null,
            done: isCompleted,
            current: false
        }
    ];

    return (
        <GeneralDialog
            show={show}
            close={onClose}
            title={<b>{translate('ABB.Powertrain.Frontend.retrainingProgressTitle')}</b>}
            closeButton={true}
            acceptButtonProps={{
                text: translate('ABB.Powertrain.Frontend.closeBtnLbl'),
                onClick: onClose,
                type: 'primary'
            }}
            modalClassName='retraining-progress-dialog'
        >
            <div className='retraining-progress-steps'>
                {steps.map((step, idx) => {
                    const isDone = step.done;
                    const isCurrent = step.current;
                    let stepModifier = '';
                    if (isDone) { stepModifier = ' progress-step--done'; }
                    else if (isCurrent) { stepModifier = ' progress-step--current'; }
                    const stepClass = `progress-step${stepModifier}`;
                    return (
                        <div key={retrainingStepKeys[idx]} className={stepClass}>
                            <div className='step-indicator'>
                                <StepCircle done={isDone} current={isCurrent} />
                                {idx < steps.length - 1 && <span className='step-connector' />}
                            </div>
                            <div className='step-content'>
                                <span className='step-title'>{step.title}</span>
                                {step.description && <span className='step-description'>{step.description}</span>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </GeneralDialog>
    );
};

RetrainingProgressModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    retrainingDetails: PropTypes.shape({
        enableRetraining: PropTypes.bool,
        retrainingStatus: PropTypes.string,
        retrainingStartDate: PropTypes.string,
        retrainingCompletedDate: PropTypes.string,
        requestedSubmittedDate: PropTypes.string,
        initiatedBy: PropTypes.string,
        isDriveAnomaly: PropTypes.bool
    })
};

const RetrainingComponent = ({
    retrainingModel,
    setRetrainingModel,
    actions,
    selectedAsset,
    retrainingStartDate,
    setRetrainingStartDate,
    showProgressModal,
    setShowProgressModal,
    retrainingDetails
}) => {

    // --- Visibility control for date picker ---
    const [pickerVisible, setPickerVisible] = useState(false);
    const pickerVisibility = {
        visible: pickerVisible,
        setVisibilityOn: () => setPickerVisible(true),
        setVisibilityOff: () => setPickerVisible(false)
    };

    const isDriveAnomaly = retrainingDetails?.isDriveAnomaly || false;

    const handleDateSelect = (date) => {
        setRetrainingStartDate(date);
    };

    const handleRetraining = () => {
        const dateString = moment(retrainingStartDate).format('YYYY-MM-DD');
        actions.getRetrainingProgress(selectedAsset?.id, dateString);
        setRetrainingModel(false);
    };

    return (
        <>
            <RetrainingDialog
                show={retrainingModel}
                onClose={() => setRetrainingModel(false)}
                onConfirm={handleRetraining}
                retrainingStartDate={retrainingStartDate}
                onDateSelect={handleDateSelect}
                pickerVisibility={pickerVisibility}
                isDriveAnomaly={isDriveAnomaly}
            />
            {showProgressModal && <RetrainingProgressModal
                show={showProgressModal}
                onClose={() => setShowProgressModal(false)}
                retrainingDetails={retrainingDetails}
            />}
        </>
    );
};

RetrainingComponent.propTypes = {
    retrainingModel: PropTypes.bool.isRequired,
    setRetrainingModel: PropTypes.func.isRequired,
    actions: PropTypes.shape({
        getRetrainingProgress: PropTypes.func.isRequired
    }).isRequired,
    selectedAsset: PropTypes.object,
    retrainingStartDate: PropTypes.any,
    setRetrainingStartDate: PropTypes.func.isRequired,
    showProgressModal: PropTypes.bool.isRequired,
    setShowProgressModal: PropTypes.func.isRequired,
    retrainingDetails: PropTypes.shape({
        enableRetraining: PropTypes.bool,
        retrainingStatus: PropTypes.string,
        retrainingStartDate: PropTypes.string,
        retrainingCompletedDate: PropTypes.string,
        requestedSubmittedDate: PropTypes.string,
        initiatedBy: PropTypes.string,
        isDriveAnomaly: PropTypes.bool
    })
};

export default RetrainingComponent;
